<script lang="ts" setup>
import { computed, Ref, ref, toRef } from 'vue';
import Chip from 'primevue/chip';
import { useToast } from 'primevue/usetoast';
import { OrganizationInterface, OrganizationsInterface, OrganizationTableInterface } from '@/types';
import AddOrganizationModal from '@/components/AddOrganizationModal/AddOrganizationModal.vue';
import OrganizationConnections from './OrganizationConnections.vue';
import { useBillingAdminOrganizationStore } from '@/store';
import { EMIT, PERMISSIONS } from '@/enum';
import { can } from '@/composables';

interface OrganizationPropsInterface {
  tableData?: OrganizationInterface[];
  billingAdminId?: number | undefined;
  loading?: boolean;
}

const props = defineProps<OrganizationPropsInterface>();
const emit = defineEmits<{ (e: EMIT.UPDATE): void }>();
const BillingAdminOrganizationStore = useBillingAdminOrganizationStore();
const organizations: Ref<OrganizationInterface[] | undefined> = toRef(props, 'tableData');
const organizationCount: Ref<number> = computed(() => (organizations.value ? organizations.value.length : 0));
const organizationModalOpen = ref(false);
const isAttach = ref(false);
const toast = useToast();
const organizationItems: Ref<OrganizationsInterface[]> = ref([]);
const addEditOrgsButtonText = computed<string>(() =>
  organizations.value?.length ? 'Edit Organizations' : 'Add Organizations',
);

const doMessage = (name?: string) => {
  const detail = isAttach.value
    ? 'Organization(s) has been added'
    : name
    ? `Organization ${name} has been removed from billing admin`
    : `Organization(s) have been removed from billing admin`;

  toast.add({
    severity: 'success',
    summary: isAttach.value ? 'Added' : 'Removed',
    detail,
    life: 3000,
  });
  emit(EMIT.UPDATE);
};

const createMenuModel = (item: OrganizationTableInterface) => {
  const last = item[item.length - 1];
  const id = last.id;
  const name = last.name;
  const menuItem = {
    label: 'Remove Organization',
    command: () => detachOrganization([id], [name]),
    visible: can([PERMISSIONS.BILLING_ADMIN_DETACH_ORGANIZATIONS]),
  };
  return [menuItem].filter((item) => item.visible);
};

const attachOrganization: (ids: number[]) => Promise<void> = async (ids) => {
  await BillingAdminOrganizationStore.attachOrganizations({
    organizations: ids,
    billingAdminId: props.billingAdminId as number,
  });

  isAttach.value = true;

  doMessage();
};

const detachOrganization: (id: number[], organizationName?: string) => Promise<void> = async (
  ids,
  organizationName,
) => {
  await BillingAdminOrganizationStore.detachOrganizations({
    organizations: ids,
    billingAdminId: props.billingAdminId as number,
  });

  isAttach.value = false;

  doMessage(organizationName);
};

const openAddOrganizations = async () => {
  if (
    can([PERMISSIONS.BILLING_ADMIN_ATTACH_ORGANIZATIONS]) &&
    can([PERMISSIONS.BILLING_ADMIN_ORGANIZATIONS_AVAILABLE_TO_ADD])
  ) {
    organizationItems.value = await BillingAdminOrganizationStore.getAddOrganizationsModalData(
      props.billingAdminId as number,
    );
  } else {
    toast.add({
      severity: 'warning',
      summary: 'Error',
      detail: 'You do not have permission to add organizations',
      life: 3000,
    });
  }
  organizationModalOpen.value = true;
};

const onCancelAdd = () => (organizationModalOpen.value = false);

const onSaveAdd = (organizations: number[]) => {
  attachOrganization(organizations);
  organizationModalOpen.value = false;
};
</script>

<template>
  <Card>
    <template #header>
      <h2>Organizations</h2>
      <Chip>{{ organizationCount }}</Chip>
      <div class="ml-auto">
        <AppButton
          v-if="can([PERMISSIONS.BILLING_ADMIN_ATTACH_ORGANIZATIONS])"
          type="text"
          size="small"
          :text="addEditOrgsButtonText"
          icon-left="add"
          @click="openAddOrganizations"
        />
      </div>
    </template>
    <template #content>
      <DataTable
        :value="organizations"
        :global-filter-fields="['name']"
        :scrollable="true"
        loading-icon="logo-spinner"
        :loading="loading"
        data-key="name"
        responsive-layout="scroll"
        scroll-height="440px"
        state-key="session-billing-admin-organization-store-table"
        state-storage="session"
        :class="{ 'table-loading': loading }"
      >
        <template v-if="organizationCount">
          <Column body-class="w-full" header-class="w-full" field="name" header="Organization">
            <template #body="{ data }">
              <OrganizationConnections :organizations="data" />
            </template>
          </Column>
          <Column
            body-class="justify-content-center mw-10rem"
            header-class="justify-content-center mw-10rem"
            field="id"
            header="Actions"
          >
            <template #body="{ data }">
              <ActionMenu
                v-if="createMenuModel(data).length > 0"
                :menu-items="createMenuModel(data)"
                icon="more_horiz"
              />
            </template>
          </Column>
        </template>
        <template #empty>Organizations not assigned</template>
      </DataTable>
      <AddOrganizationModal
        v-if="organizationModalOpen && can([PERMISSIONS.BILLING_ADMIN_ORGANIZATIONS_AVAILABLE_TO_ADD])"
        :title="addEditOrgsButtonText"
        :items="organizationItems"
        :searchable="true"
        :multi-selection="true"
        :selected-items-data="getUsersCurrentOrganizations"
        @save="onSaveAdd"
        @cancel="onCancelAdd"
      />
    </template>
  </Card>
</template>

<style lang="scss" scoped></style>
